import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Plus, MoreVertical, Pencil, Trash2, Building2 } from "lucide-react";
import type { Carrier, InsertCarrier } from "@shared/schema";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { US_STATES } from "@/lib/usStates";
import { ChevronDown, ChevronUp, MapPin } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

export default function Carriers() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCarrier, setEditingCarrier] = useState<Carrier | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statesOpen, setStatesOpen] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Available carrier types
  const carrierTypes = ["health", "dental", "vision", "life", "disability", "voluntary"];

  // Form state
  const [formData, setFormData] = useState<Partial<InsertCarrier>>({
    name: "",
    types: [],
    wordBrownId: "",
    description: "",
    isActive: true,
    availableStates: [],
  });

  // Fetch carriers
  const { data: carriers = [], isLoading } = useQuery<Carrier[]>({
    queryKey: ["/api/carriers"],
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: Partial<InsertCarrier>) => {
      return apiRequest("POST", "/api/carriers", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/carriers"] });
      handleCloseDialog();
      toast({
        title: "Success",
        description: "Carrier created successfully",
      });
    },
    onError: (error: any) => {
      console.error("Carrier creation error:", error);
      toast({
        title: "Error",
        description: error?.details || error?.message || "Failed to create carrier",
        variant: "destructive",
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<InsertCarrier> }) => {
      return apiRequest("PUT", `/api/carriers/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/carriers"] });
      handleCloseDialog();
      toast({
        title: "Success",
        description: "Carrier updated successfully",
      });
    },
    onError: (error: any) => {
      console.error("Carrier update error:", error);
      toast({
        title: "Error",
        description: error?.details || error?.message || "Failed to update carrier",
        variant: "destructive",
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("DELETE", `/api/carriers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/carriers"] });
      toast({
        title: "Success",
        description: "Carrier deleted successfully",
      });
    },
    onError: (error: any) => {
      console.error("Carrier delete error:", error);
      toast({
        title: "Error",
        description: error?.message || "Failed to delete carrier",
        variant: "destructive",
      });
    },
  });

  const handleOpenDialog = (carrier?: Carrier) => {
    if (carrier) {
      setEditingCarrier(carrier);
      setFormData({
        name: carrier.name,
        types: carrier.types || [],
        wordBrownId: carrier.wordBrownId || "",
        description: carrier.description || "",
        isActive: carrier.isActive ?? true,
        availableStates: carrier.availableStates || [],
      });
      // Expand states section if carrier has states configured
      setStatesOpen((carrier.availableStates?.length ?? 0) > 0);
    } else {
      setEditingCarrier(null);
      setFormData({
        name: "",
        types: [],
        wordBrownId: "",
        description: "",
        isActive: true,
        availableStates: [],
      });
      setStatesOpen(false);
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingCarrier(null);
    setFormData({
      name: "",
      types: [],
      wordBrownId: "",
      description: "",
      isActive: true,
      availableStates: [],
    });
    setStatesOpen(false);
  };

  const handleTypeToggle = (type: string) => {
    const currentTypes = formData.types || [];
    const newTypes = currentTypes.includes(type)
      ? currentTypes.filter((t) => t !== type)
      : [...currentTypes, type];
    setFormData({ ...formData, types: newTypes });
  };

  const handleStateToggle = (stateCode: string) => {
    const currentStates = formData.availableStates || [];
    const newStates = currentStates.includes(stateCode)
      ? currentStates.filter((s) => s !== stateCode)
      : [...currentStates, stateCode];
    setFormData({ ...formData, availableStates: newStates });
  };

  const handleSelectAllStates = () => {
    setFormData({ ...formData, availableStates: US_STATES.map((s) => s.code) });
  };

  const handleClearAllStates = () => {
    setFormData({ ...formData, availableStates: [] });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Clean up empty strings to null
    const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
      acc[key] = value === "" ? null : value;
      return acc;
    }, {} as any);

    if (editingCarrier) {
      updateMutation.mutate({ id: editingCarrier.id, data: cleanedData });
    } else {
      createMutation.mutate(cleanedData);
    }
  };

  const handleDelete = (id: number, name: string) => {
    if (confirm(`Are you sure you want to delete ${name}? This action cannot be undone.`)) {
      deleteMutation.mutate(id);
    }
  };

  // Filter carriers based on search
  const filteredCarriers = carriers.filter((carrier) =>
    carrier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    carrier.types?.some(type => type.toLowerCase().includes(searchTerm.toLowerCase())) ||
    carrier.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeCarriers = filteredCarriers
    .filter((c) => c.isActive)
    .sort((a, b) => a.name.localeCompare(b.name));
  const inactiveCarriers = filteredCarriers
    .filter((c) => !c.isActive)
    .sort((a, b) => a.name.localeCompare(b.name));

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-muted-foreground">Loading carriers...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Carriers</h1>
          <p className="text-muted-foreground mt-1">
            Manage insurance carriers and their contact information
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              Add Carrier
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="carrier-dialog-description">
            <DialogHeader>
              <DialogTitle>
                {editingCarrier ? "Edit Carrier" : "Add New Carrier"}
              </DialogTitle>
              <p id="carrier-dialog-description" className="text-sm text-muted-foreground">
                {editingCarrier ? "Update carrier information and coverage types." : "Add a new insurance carrier with coverage types."}
              </p>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="name">
                      Carrier Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      placeholder="e.g., Blue Cross Blue Shield"
                    />
                  </div>

                  <div className="col-span-2">
                    <Label>
                      Carrier Types <span className="text-red-500">*</span>
                    </Label>
                    <div className="grid grid-cols-3 gap-3 mt-2">
                      {carrierTypes.map((type) => (
                        <div key={type} className="flex items-center space-x-2">
                          <Checkbox
                            id={`type-${type}`}
                            checked={formData.types?.includes(type)}
                            onCheckedChange={() => handleTypeToggle(type)}
                          />
                          <Label
                            htmlFor={`type-${type}`}
                            className="text-sm font-normal capitalize cursor-pointer"
                          >
                            {type}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {formData.wordBrownId && (
                    <div>
                      <Label htmlFor="wordBrownId">Legacy Import ID</Label>
                      <Input
                        id="wordBrownId"
                        value={formData.wordBrownId}
                        disabled
                        className="bg-muted cursor-not-allowed"
                      />
                    </div>
                  )}

                  <div className="col-span-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, description: e.target.value })
                      }
                      placeholder="Additional information about this carrier"
                      rows={3}
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="isActive"
                      checked={formData.isActive ?? true}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, isActive: checked })
                      }
                    />
                    <Label htmlFor="isActive">Active Carrier</Label>
                  </div>
                </div>
              </div>

              {/* State Availability Section */}
              <Collapsible open={statesOpen} onOpenChange={setStatesOpen}>
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    type="button"
                    className="flex items-center justify-between w-full p-0 h-auto hover:bg-transparent"
                  >
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      <span className="text-sm font-semibold">State Availability</span>
                      {(formData.availableStates?.length ?? 0) > 0 && (
                        <Badge variant="secondary" className="ml-2">
                          {formData.availableStates?.length} selected
                        </Badge>
                      )}
                      {(formData.availableStates?.length ?? 0) === 0 && (
                        <span className="text-xs text-muted-foreground ml-2">
                          (All states if none selected)
                        </span>
                      )}
                    </div>
                    {statesOpen ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-4">
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleSelectAllStates}
                      >
                        Select All
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleClearAllStates}
                      >
                        Clear All
                      </Button>
                    </div>
                    <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto border rounded-md p-3">
                      {US_STATES.map((state) => (
                        <div key={state.code} className="flex items-center space-x-2">
                          <Checkbox
                            id={`state-${state.code}`}
                            checked={formData.availableStates?.includes(state.code)}
                            onCheckedChange={() => handleStateToggle(state.code)}
                          />
                          <Label
                            htmlFor={`state-${state.code}`}
                            className="text-xs font-normal cursor-pointer"
                            title={state.name}
                          >
                            {state.code}
                          </Label>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Leave empty to make carrier available in all states. Select specific states to limit availability.
                    </p>
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseDialog}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {editingCarrier ? "Update Carrier" : "Create Carrier"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <Input
              placeholder="Search carriers by name, type, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-md"
            />
            <div className="text-sm text-muted-foreground">
              {filteredCarriers.length} carrier{filteredCarriers.length !== 1 ? "s" : ""}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active Carriers Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Active Carriers ({activeCarriers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activeCarriers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No active carriers found.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>States</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeCarriers.map((carrier) => (
                  <TableRow key={carrier.id}>
                    <TableCell className="font-medium">{carrier.name}</TableCell>
                    <TableCell>
                      {carrier.types && carrier.types.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {carrier.types.map((type) => (
                            <Badge key={type} variant="outline" className="capitalize">
                              {type}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {carrier.availableStates && carrier.availableStates.length > 0 ? (
                        <span className="text-sm" title={carrier.availableStates.join(", ")}>
                          {carrier.availableStates.length <= 5
                            ? carrier.availableStates.join(", ")
                            : `${carrier.availableStates.slice(0, 5).join(", ")}... (+${carrier.availableStates.length - 5})`}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-sm">All states</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {carrier.description ? (
                        <span className="text-sm">{carrier.description}</span>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleOpenDialog(carrier)}
                          >
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDelete(carrier.id, carrier.name)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Inactive Carriers */}
      {inactiveCarriers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-muted-foreground" />
              Inactive Carriers ({inactiveCarriers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>States</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inactiveCarriers.map((carrier) => (
                  <TableRow key={carrier.id} className="opacity-60">
                    <TableCell className="font-medium">{carrier.name}</TableCell>
                    <TableCell>
                      {carrier.types && carrier.types.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {carrier.types.map((type) => (
                            <Badge key={type} variant="outline" className="capitalize">
                              {type}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {carrier.availableStates && carrier.availableStates.length > 0 ? (
                        <span className="text-sm" title={carrier.availableStates.join(", ")}>
                          {carrier.availableStates.length <= 5
                            ? carrier.availableStates.join(", ")
                            : `${carrier.availableStates.slice(0, 5).join(", ")}... (+${carrier.availableStates.length - 5})`}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-sm">All states</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {carrier.description ? (
                        <span className="text-sm">{carrier.description}</span>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleOpenDialog(carrier)}
                          >
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDelete(carrier.id, carrier.name)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
